import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/auth';
import { useEffect, useRef, useCallback } from 'react';
import { isMockSession } from '@/mocks';

let socket: Socket | null = null;
let socketToken: string | null = null;

/**
 * Get or create the singleton Socket.IO connection.
 * Automatically authenticates with the current JWT token.
 */
function getSocket(): Socket | null {
	// The test-user demo session has no backend — never open a socket.
	if (isMockSession()) return null;

	const token = useAuthStore.getState().token;
	if (!token) return null;

	// Recreate only when auth token changes.
	if (socket && socketToken === token) {
		return socket;
	}

	if (socket) {
		socket.disconnect();
		socket = null;
	}

	socketToken = token;

	socket = io(window.location.origin, {
		path: '/socket.io',
		auth: { token },
		// Keep a single long-lived WebSocket; avoid polling fallback
		transports: ['websocket', 'polling'],
		reconnection: true,
		reconnectionAttempts: 5,
		reconnectionDelay: 1000
	});

	socket.on('connect', () => {
		console.log('🔌 Socket.IO connected:', socket?.id);
	});

	socket.on('disconnect', (reason) => {
		console.log('🔌 Socket.IO disconnected:', reason);
	});

	socket.on('connect_error', (error) => {
		console.error('🔌 Socket.IO connection error:', error.message);
	});

	return socket;
}

/**
 * Disconnect the existing socket (e.g., on logout).
 */
export function disconnectSocket(): void {
	if (socket) {
		socket.disconnect();
		socket = null;
	}
}

/**
 * Hook to listen for candidate vault updates via Socket.IO.
 * Automatically manages the socket lifecycle.
 *
 * @param signalhireId - If provided, only triggers callback for updates matching this ID
 * @param onUpdate - Callback fired when a candidate vault update is received
 */
export function useCandidateVaultUpdates(
	signalhireId: string | null | undefined,
	onUpdate: (data: { signalhireId: string; data: any }) => void
): void {
	const callbackRef = useRef(onUpdate);
	callbackRef.current = onUpdate;

	const handleEvent = useCallback((payload: { signalhireId: string; data: any }) => {
		if (!signalhireId || payload.signalhireId === signalhireId) {
			callbackRef.current(payload);
		}
	}, [signalhireId]);

	useEffect(() => {
		const s = getSocket();
		if (!s) return;

		s.on('candidate-vault:updated', handleEvent);

		return () => {
			s.off('candidate-vault:updated', handleEvent);
		};
	}, [handleEvent]);
}
